import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { useApiClient, useFetch, useFetchImmutable } from '@marageti/z4-lib';
import {
  Partner,
  SalesRecording,
  TripHistories,
  TripMatch,
  TripMatchStatus,
  TripRequest,
  TripRequestStatus,
} from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import TripDetailsView from './trip-details-view';
import { TripError } from '../../components/trips';
import { formatCountryNames, joinDestinationNames } from '../../utils/helpers';
import { useAuthStore } from '../../store';
import debugPromise from '../../utils/debug-promise';
import { canInviteCoTravelers as getCanInviteCoTravelers } from '../../utils/trip-requests';

const TripDetailsContainer = () => {
  const { tripRequestId } = useParams();
  const navigate = useNavigate();
  const { loggedInTraveler } = useAuthStore();
  const travelerId = loggedInTraveler?.id;

  const apiClient = useApiClient();

  const [allSalesRecordings, setAllSalesRecordings] = useState<{ [key: string]: SalesRecording[] }>({});
  const [salesRecordingsLoading, setSalesRecordingsLoading] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [viewingCoTravelerCardId, setViewingCoTravelerCardId] = useState<string | null>(null);
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const { data: tripHistories } = useFetchImmutable<TripHistories>(apiClient.peopleClient.getTravelerTripHistories, [travelerId], !!travelerId);

  const {
    data: tripRequest,
    isLoading: tripRequestLoading,
    mutate,
  } = useFetch<TripRequest>(apiClient.tripRequestsClient.getTripRequest, [tripRequestId], !!tripRequestId);

  const {
    data: matches,
    isLoading: matchesLoading,
  } = useFetch<TripMatch[]>(apiClient.tripRequestsClient.getTripRequestTripMatches, [tripRequestId], !!tripRequestId);

  const toggleCancel = () => { navigate(`/trip-requests/${tripRequestId}/cancel`); window.scrollTo(0, 0); };
  const toggleInvite = () => { setIsInviting(!isInviting); window.scrollTo(0, 0); };
  const openCoTravelerCard = (coTravelerId: string | null) => { setViewingCoTravelerCardId(coTravelerId); };
  const activeMatches = useMemo(() => {
    if (matchesLoading || !matches || !countryCodes) return [];

    const activeStatuses = [
      TripMatchStatus.MatchActive,
      TripMatchStatus.MatchPostponed,
      TripMatchStatus.MatchInactive,
      TripMatchStatus.MatchSold,
      TripMatchStatus.MatchPartnerCanceled,
      TripMatchStatus.MatchTravelerCanceled,
      TripMatchStatus.MatchSoldCanceled,
      TripMatchStatus.MatchRefundedCanceled,
    ];

    return matches.filter((match) => activeStatuses.includes(match.status));
  }, [matches, matchesLoading, countryCodes]);

  const partnerIds = useMemo(
    () => activeMatches.map((match) => match.partnerId).filter(Boolean),
    [activeMatches],
  );

  const { data: partners = [], isLoading: partnersLoading } = useSWR<Partner[]>(
    partnerIds.length > 0 ? ['partners', partnerIds.join(',')] : null,
    async () => {
      const fetchedPartners = await Promise.all(
        partnerIds.map((partnerId) => apiClient.partnersClient.getPartner(partnerId, { params: { inactive: false } })),
      );
      return fetchedPartners;
    },
  );

  const soldMatches = useMemo(
    () => activeMatches.filter((match) => match.status === TripMatchStatus.MatchSold),
    [activeMatches],
  );

  const fetchSalesRecordings = async (matchesToFetch: TripMatch[]) => {
    setSalesRecordingsLoading(true);
    try {
      const recordings = await Promise.allSettled(
        matchesToFetch.map(async (match) => {
          try {
            const matchRecordings = await apiClient.tripMatchesClient.getTripMatchSalesRecordings(match.id);
            return { matchId: match.id, recordings: matchRecordings };
          } catch (error) {
            console.error(`Error fetching recordings for match ${match.id}:`, error);
            return { matchId: match.id, recordings: [] };
          }
        }),
      );

      const newRecordings = recordings.reduce((acc, result) => {
        if (result.status === 'fulfilled') {
          acc[result.value.matchId] = result.value.recordings;
        }
        return acc;
      }, {} as { [key: string]: SalesRecording[] });

      setAllSalesRecordings(newRecordings);
    } catch (error) {
      console.error('Error fetching sales recordings:', error);
      setAllSalesRecordings({});
    } finally {
      setSalesRecordingsLoading(false);
    }
  };

  const titleCountry = useMemo(() => {
    if (!tripRequest) return '';

    // If the trip is not sold, derive title from tripRequest.destinations
    if (tripRequest.status !== TripRequestStatus.SoldStatus) {
      return joinDestinationNames(tripRequest.destinations, ', ');
    }

    // If sold, compute the country list from soldMatches
    const allSoldCountries = Array.from(
      new Set(soldMatches.flatMap((match) => match.countries ?? [])),
    );

    return allSoldCountries.length > 0 ? formatCountryNames(allSoldCountries, countryCodes) : '';
  }, [tripRequest, soldMatches, countryCodes]);

  useEffect(() => {
    if (soldMatches.length) {
      debugPromise('tripDetailsContainer:soldMatches.fetchSalesRecordings', fetchSalesRecordings(soldMatches));
    }
  }, [soldMatches]);

  const isCooperative = matches?.some((match) => match.cooperative) || false;
  const canInviteCoTravelers = useMemo(
    () => getCanInviteCoTravelers(tripRequest, {
      tripHistories,
      soldMatchReturnDates: soldMatches
        .map((match) => match.returnDate)
        .filter((date): date is string => !!date),
    }),
    [tripRequest, tripHistories, soldMatches],
  );

  if (!tripRequest && !tripRequestLoading) {
    return <TripError />;
  }

  if (!tripRequest || !soldMatches) {
    return null;
  }

  return (
    <TripDetailsView
      activeMatches={activeMatches}
      partners={partners}
      canInviteCoTravelers={canInviteCoTravelers}
      cooperative={isCooperative}
      photoId={tripRequest.photoId}
      isLoading={tripRequestLoading || matchesLoading || salesRecordingsLoading || partnersLoading}
      salesRecordings={allSalesRecordings}
      soldMatches={soldMatches}
      status={tripRequest.status}
      titleCountry={titleCountry}
      tripRequest={tripRequest}
      travelerId={travelerId || ''}
      toggleCancel={toggleCancel}
      isInviting={isInviting}
      toggleInvite={toggleInvite}
      openCoTravelerCard={openCoTravelerCard}
      viewingCoTravelerCardId={viewingCoTravelerCardId}
      mutate={mutate}
    />
  );
};

export default TripDetailsContainer;
